// src/components/Auth/SignupModal.jsx
import React, { useState } from 'react';
import { signUp } from '../../services/auth';
import { Link } from 'react-router-dom';

const SignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const { firstName, lastName, email, password, confirmPassword } = formData;

        if (!firstName || !lastName || !email || !password) {
            setError('Please fill in all fields');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            setLoading(false);
            return;
        }

        const userData = { firstName, lastName };
        const result = await signUp(email, password, userData);

        if (result.success) {
            onClose();
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
            alert('Account created successfully! Please login.');
            if (onSwitchToLogin) onSwitchToLogin();
        } else {
            setError(result.error);
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl z-10">
                    <i className="fas fa-times"></i>
                </button>

                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-user-graduate text-white text-2xl"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-gradpath-dark">Create Your Account</h2>
                        <p className="text-gray-500 text-sm">Join GradPath and start your career journey</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative">
                                    <i className="fas fa-user absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                    <input
                                        type="text"
                                        id="firstName"
                                        placeholder="First Name *"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                        required
                                    />
                                </div>
                                <div className="relative">
                                    <i className="fas fa-user absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                    <input
                                        type="text"
                                        id="lastName"
                                        placeholder="Last Name *"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="relative">
                                <i className="fas fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder="Email Address *"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    required
                                />
                            </div>

                            <div className="relative">
                                <i className="fas fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                <input
                                    type="password"
                                    id="password"
                                    placeholder="Password * (min 6 characters)"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    required
                                />
                            </div>

                            <div className="relative">
                                <i className="fas fa-check-circle absolute left-3 top-1/2 -translate-y-1/2 text-gradpath-green"></i>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    placeholder="Confirm Password *"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    required
                                />
                            </div>

                            {error && (
                                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                            >
                                {loading ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-user-plus mr-2"></i> Sign Up</>}
                            </button>
                        </div>
                    </form>

                    <div className="text-center mt-6 pt-4 border-t border-gray-200 space-y-3">
                        <p className="text-sm text-gray-600">
                            Already have an account?{' '}
                            <button onClick={onSwitchToLogin} className="text-gradpath-green font-semibold hover:underline">
                                Login here
                            </button>
                        </p>
                        <div className="pt-2">
                            <Link
                                to="/for-recruiters"
                                onClick={onClose}
                                className="text-sm text-gradpath-green hover:underline font-medium inline-flex items-center gap-1"
                            >
                                <i className="fas fa-building text-xs"></i>
                                Sign up as Recruiter
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupModal;